import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { e2eAgentApi } from "../api/e2eAgentClient";
import type { components } from "../api/e2eAgentSchema";

export type WorkspaceUser = components["schemas"]["WorkspaceUserOut"];
export type WorkspaceAccessLevel =
  components["schemas"]["WorkspaceAccessLevel"];
export type CreateWorkspaceUser =
  components["schemas"]["CreateWorkspaceUserRequest"];
export type UpdateWorkspaceUser =
  components["schemas"]["UpdateWorkspaceUserRequest"];

const queryKey = ["e2e-agent", "workspace-users"] as const;

function ensure<Data>(data: Data | undefined, error: unknown): Data {
  if (error !== undefined) {
    const detail =
      error && typeof error === "object" && "detail" in error
        ? JSON.stringify(error.detail)
        : JSON.stringify(error);
    throw new Error(`User request failed: ${detail}`);
  }
  if (data === undefined) throw new Error("User response was empty.");
  return data;
}

export function useWorkspaceUsers() {
  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/workspace/users");
      return ensure(data, error);
    },
  });
}

export function useCreateWorkspaceUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateWorkspaceUser) => {
      const { data, error } = await e2eAgentApi.POST("/v1/workspace/users", {
        body,
      });
      return ensure(data, error);
    },
    onSuccess: async () => client.invalidateQueries({ queryKey }),
  });
}

export function useUpdateWorkspaceUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      subject,
      update,
    }: {
      subject: string;
      update: UpdateWorkspaceUser;
    }) => {
      const { data, error } = await e2eAgentApi.PATCH(
        "/v1/workspace/users/{subject}",
        {
          params: { path: { subject } },
          body: update,
        },
      );
      return ensure(data, error);
    },
    onSuccess: async () => client.invalidateQueries({ queryKey }),
  });
}

function useLifecycleMutation(
  path: "suspend" | "reactivate" | "resend-invitation",
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (subject: string) => {
      const { data, error } = await e2eAgentApi.POST(
        `/v1/workspace/users/{subject}/${path}`,
        { params: { path: { subject } } },
      );
      return ensure(data, error);
    },
    onSuccess: async () => client.invalidateQueries({ queryKey }),
  });
}

export function useSuspendWorkspaceUser() {
  return useLifecycleMutation("suspend");
}

export function useReactivateWorkspaceUser() {
  return useLifecycleMutation("reactivate");
}

export function useResendWorkspaceInvitation() {
  return useLifecycleMutation("resend-invitation");
}
